import React, { useEffect, useCallback, useState } from 'react';
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Location } from 'history';
import { callAlertWeb, callNativeWeb, footerShowEventWeb } from 'common/appInterface';
import client from 'lib/CommonApi/client';
import { getVersionUrl } from 'common/cacheOverwrite';
import { CardProductCodeName, PostDeliveryCardProductCodeList } from '../../constants/Card';

const ApplyCompletPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL16";
  const screenName = t("APPL16.title02");

  const [cardApply] = useState<Partial<CardApplyCommon>>(location.state.apply);

  /** 신청 요약 (서버 응답) */
  const [summary, setSummary] = useState<CardApplySummary | null>(null);
  /** 선택한 디자인의 카드 이미지 (인트로에서 localStorage에 저장한 상품·디자인별 이미지 경로) */
  const cardImageUrl = summary ? localStorage.getItem(`${summary.cardProductCode}_${summary.designDivisionCode}`) : null;
  /** 우편배송 전환 상품 여부 (요약 응답 전에는 신청 정보의 상품코드로 판단) */
  const isPostDelivery = PostDeliveryCardProductCodeList.includes(Number(summary?.cardProductCode ?? cardApply.cardProductCode));

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate("/main", { state: { query: location.state.query, swiperIndex: 0, tabIndex: "1" }});
  }, [navigate, location]);

  /** 신청완료 후처리 호출 */
  const callComplet = useCallback(async () => {
    await client.patch("/api/app/apply/complet", { ...location.state.query, receiveSeq: cardApply.receiveSeq, receiveDate: cardApply.receiveDate }).then((response) => {
      let result = response.data.response;
      console.log(result);

      // 요약 정보가 있을 때만 표시
      if (response.data.code === "00" && result?.cardProductCode) {
        setSummary(result);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    });
  }, [location, cardApply]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callComplet();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents notification2 pb64-mb">
          <div className="inner">
            <div className="applyInfoWrap">
              <div className="picto">
                {cardImageUrl && (
                  <img src={getVersionUrl(cardImageUrl)}
                       alt={t(`APPL01.name.${summary?.cardProductCode}`, CardProductCodeName[Number(summary?.cardProductCode)])}
                  />
                )}
              </div>
              <p className="mainTxt">{t('APPL16.title')}</p>
              <p className="subTxt">
                {isPostDelivery
                  ? t('APPL16.explan09', 'Your card will arrive in your mailbox in about 7 business days.')
                  : t('APPL16.explan04')}
              </p>
            </div>

            {/* 신청 요약 */}
            {summary && (
              <dl className="applySummary">
                <dt>{t('APPL16.info01', 'Card')}</dt>
                <dd>{t(`APPL01.name.${summary.cardProductCode}`, CardProductCodeName[Number(summary.cardProductCode)])}</dd>

                {/* 월회비 유형·금액이 모두 있을 때만 (Welcome 등 월회비 없는 상품은 금액이 비어 옴) */}
                {summary.monthlyFeePayTypeCode && summary.monthlyFee && (
                  <>
                    <dt>{t('APPL16.info02', 'Card Monthly Fee Type')}</dt>
                    <dd>{t(`SERV17.header${summary.monthlyFeePayTypeCode}`)}</dd>
                    <dd>{summary.monthlyFee} {t('COMMON.krw')}</dd>
                  </>
                )}

                <dt>{t('APPL16.info03', 'Card Delivery Address')}</dt>
                <dd>
                  {summary.deliverySelectCode === '1'
                    ? t('APPL16.info04', 'Photo Submitted')
                    : <>
                        {summary.homeNewAddress}
                        {summary.homeNewDetailAddress && ` ${summary.homeNewDetailAddress}`}
                      </>
                    }
                </dd>
                {isPostDelivery
                  /* 우편배송 전환 상품은 수령 방법 대신 배송유형 태그 */
                  ? <dd><span className="deliveryTag">{t('APPL16.info05.03', 'Mailbox delivery')}</span></dd>
                  /* 수령 방법 값이 없으면 기본 02(우편함·현관 수령)로 표시 */
                  : <dd>{t(`APPL16.info05.${summary?.receiveMethod || '02'}`)}</dd>}
              </dl>
            )}
            {/* 카드 수령 후 안내 */}
            <div className="info">
              <p className="infoTit">{t('APPL16.explan07', 'When you receive the card')}</p>
              <p className="infoTxt">{t('APPL16.explan08')}</p>
            </div>
            {/* 고객센터 */}
            <p className="customerCenter">{t('APPL16.explan06')}<span>1670-4565</span></p>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" onClick={() => {
              navigate("/apply/complet/" + (summary?.cardProductCode ?? cardApply.cardProductCode), { state: { query: location.state.query, swiperIndex: 0, tabIndex: "1" }});
            }}><span>{t("COMMON.button.done")}</span></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyCompletPage;